/**
 * Lay out QR labels in millimetres for a media preset.
 *
 * The code is sized to a whole number of printer dots per module, so a thermal printer never has
 * to split a module across dots. HTML output is used for browser printing (vector, the driver
 * rasterises); canvas output is used for native bridges that need an image.
 */

const libMatrix = require('../rendering/QR-Matrix.js');

const POINTS_PER_MM = 72 / 25.4;
const GAP_MM = 2;

/**
 * @typedef {Object} LabelContent
 * @property {string} Payload - Text encoded in the code.
 * @property {Array<string>} [Lines] - Text printed beside or under the code; the first line is emphasised.
 * @property {string} [Image] - A finished label image (data URL), printed fitted to the label instead of laying out Payload and Lines.
 * @property {number} [ImageWidth] - The image's pixel size; when its orientation differs from the label's, it is turned a quarter.
 * @property {number} [ImageHeight]
 */

/**
 * @typedef {Object} LabelPlan
 * @property {import('../rendering/QR-Matrix.js').QRMatrix} Matrix
 * @property {number} ModuleMM
 * @property {number} CodeMM - Printed width of the code including its quiet zone.
 * @property {number} DotsPerModule
 * @property {number} FontPT
 * @property {Array<{ Code: string, Message: string }>} Warnings
 */

/**
 * @param {import('./Label-Media-Presets.js').MediaPreset} pPreset
 * @param {LabelContent} pLabel
 * @param {Object} [pOptions]
 * @param {'L'|'M'|'Q'|'H'} [pOptions.ErrorCorrection]
 * @param {number} [pOptions.MinModuleMM] - Below this a warning is raised. Defaults to 0.5.
 * @return {LabelPlan}
 */
function planLabel(pPreset, pLabel, pOptions)
{
	const tmpOptions = pOptions || {};
	const tmpMinModuleMM = tmpOptions.MinModuleMM || 0.5;
	const tmpMatrix = libMatrix.createMatrix(pLabel.Payload, { ErrorCorrection: tmpOptions.ErrorCorrection || 'Q' });
	const tmpTotalModules = tmpMatrix.Size + (2 * libMatrix.QUIET_ZONE_MODULES);
	const tmpMargin = pPreset.MarginMM || 2;
	const tmpInnerWidth = pPreset.LabelWidthMM - (2 * tmpMargin);
	const tmpInnerHeight = pPreset.LabelHeightMM - (2 * tmpMargin);
	const tmpLineCount = Math.max(1, (pLabel.Lines || []).length);

	let tmpCodeSpace = 0;
	if (pPreset.Layout === 'Side')
	{
		tmpCodeSpace = Math.min(tmpInnerHeight, tmpInnerWidth * 0.5);
	}
	else
	{
		const tmpTextReserve = Math.min(tmpInnerHeight * 0.4, tmpLineCount * 4.5);
		tmpCodeSpace = Math.min(tmpInnerWidth, tmpInnerHeight - tmpTextReserve - GAP_MM);
	}

	const tmpDots = Math.max(1, Math.floor((tmpCodeSpace / tmpTotalModules) * pPreset.DPI / 25.4));
	const tmpModuleMM = (tmpDots * 25.4) / pPreset.DPI;
	const tmpCodeMM = tmpModuleMM * tmpTotalModules;

	const tmpTextHeight = (pPreset.Layout === 'Side') ? tmpInnerHeight : (tmpInnerHeight - tmpCodeMM - GAP_MM);
	const tmpLineHeightMM = Math.min(tmpTextHeight / tmpLineCount, 4.5);
	const tmpFontPT = Math.max(5, Math.round((tmpLineHeightMM * POINTS_PER_MM * 0.78) * 10) / 10);

	const tmpWarnings = [];
	if (tmpModuleMM < tmpMinModuleMM)
	{
		tmpWarnings.push({ Code: 'ModuleTooSmall', Message: `Each square prints at ${tmpModuleMM.toFixed(2)} mm; phones need at least ${tmpMinModuleMM} mm. Use larger labels or a shorter code.` });
	}
	if (tmpLineHeightMM < 2.2)
	{
		tmpWarnings.push({ Code: 'TextTooSmall', Message: 'The text lines will print very small on this label.' });
	}
	return { Matrix: tmpMatrix, ModuleMM: tmpModuleMM, CodeMM: tmpCodeMM, DotsPerModule: tmpDots, FontPT: tmpFontPT, Warnings: tmpWarnings };
}

/**
 * @param {number} pValue
 * @return {string}
 */
function mm(pValue)
{
	return `${Math.round(pValue * 1000) / 1000}mm`;
}

/**
 * One label as HTML, sized in millimetres.
 *
 * @param {import('./Label-Media-Presets.js').MediaPreset} pPreset
 * @param {LabelContent} pLabel
 * @param {Object} [pOptions] - Passed to planLabel.
 * @return {{ HTML: string, Plan: LabelPlan }}
 */
function renderLabelHTML(pPreset, pLabel, pOptions)
{
	if (pLabel.Image)
	{
		return { HTML: renderImageLabelHTML(pPreset, pLabel), Plan: null };
	}
	const tmpPlan = planLabel(pPreset, pLabel, pOptions);
	const tmpSVG = libMatrix.toSVG(tmpPlan.Matrix, { Width: mm(tmpPlan.CodeMM) });
	const tmpLines = (pLabel.Lines || []).map((pLine, pIndex) =>
		`<div class="hlqr-label-line${pIndex === 0 ? ' hlqr-label-line--first' : ''}">${libMatrix.escapeXML(pLine)}</div>`).join('');
	const tmpHTML = `<div class="hlqr-label hlqr-label--${pPreset.Layout === 'Side' ? 'side' : 'stack'}" style="width:${mm(pPreset.LabelWidthMM)};height:${mm(pPreset.LabelHeightMM)};padding:${mm(pPreset.MarginMM || 2)};">`
		+ `<div class="hlqr-label-code">${tmpSVG}</div>`
		+ `<div class="hlqr-label-text" style="font-size:${tmpPlan.FontPT}pt;">${tmpLines}</div>`
		+ `</div>`;
	return { HTML: tmpHTML, Plan: tmpPlan };
}

/**
 * @param {import('./Label-Media-Presets.js').MediaPreset} pPreset
 * @param {LabelContent} pLabel
 * @return {boolean} true when the image fits better turned a quarter
 */
function isImageTurned(pPreset, pLabel)
{
	if (!pLabel.ImageWidth || !pLabel.ImageHeight || (pLabel.ImageWidth === pLabel.ImageHeight) || (pPreset.LabelWidthMM === pPreset.LabelHeightMM))
	{
		return false;
	}
	return (pLabel.ImageWidth > pLabel.ImageHeight) !== (pPreset.LabelWidthMM > pPreset.LabelHeightMM);
}

/**
 * A finished label image, fitted inside the label's margins.
 *
 * @param {import('./Label-Media-Presets.js').MediaPreset} pPreset
 * @param {LabelContent} pLabel
 * @return {string}
 */
function renderImageLabelHTML(pPreset, pLabel)
{
	const tmpMargin = pPreset.MarginMM || 2;
	const tmpInnerWidth = pPreset.LabelWidthMM - (2 * tmpMargin);
	const tmpInnerHeight = pPreset.LabelHeightMM - (2 * tmpMargin);
	const tmpSource = libMatrix.escapeXML(pLabel.Image);
	const tmpImage = isImageTurned(pPreset, pLabel)
		? `<img src="${tmpSource}" alt="" style="position:absolute;left:50%;top:50%;width:${mm(tmpInnerHeight)};height:${mm(tmpInnerWidth)};object-fit:contain;transform:translate(-50%,-50%) rotate(90deg);">`
		: `<img src="${tmpSource}" alt="" style="width:100%;height:100%;object-fit:contain;${pPreset.Continuous ? 'object-position:center top;' : ''}">`;
	return `<div class="hlqr-label hlqr-label--image" style="width:${mm(pPreset.LabelWidthMM)};height:${mm(pPreset.LabelHeightMM)};padding:${mm(tmpMargin)};">`
		+ `<div class="hlqr-label-image">${tmpImage}</div></div>`;
}

/**
 * A complete print job: pages of labels plus the `@page` rule for the media.
 *
 * @param {import('./Label-Media-Presets.js').MediaPreset} pPreset
 * @param {Array<LabelContent>} pLabels
 * @param {Object} [pOptions]
 * @param {number} [pOptions.StartIndex] - Sheets only: first cell to use on the first sheet (0-based), for partly used sheets.
 * @return {{ HTML: string, PageCSS: string, Plans: Array<LabelPlan> }}
 */
function renderPrintJob(pPreset, pLabels, pOptions)
{
	const tmpOptions = pOptions || {};
	const tmpPlans = [];
	const tmpRendered = pLabels.map((pLabel) =>
	{
		const tmpLabel = renderLabelHTML(pPreset, pLabel, tmpOptions);
		if (tmpLabel.Plan)
		{
			tmpPlans.push(tmpLabel.Plan);
		}
		return tmpLabel.HTML;
	});

	if (pPreset.Kind !== 'Sheet')
	{
		const tmpPages = tmpRendered.map((pHTML) => `<div class="hlqr-print-page" style="width:${mm(pPreset.LabelWidthMM)};height:${mm(pPreset.LabelHeightMM)};">${pHTML}</div>`).join('');
		return { HTML: tmpPages, PageCSS: `@page { size: ${mm(pPreset.LabelWidthMM)} ${mm(pPreset.LabelHeightMM)}; margin: 0; }`, Plans: tmpPlans };
	}

	const tmpPerSheet = pPreset.Columns * pPreset.Rows;
	const tmpStart = Math.max(0, Math.min(tmpPerSheet - 1, tmpOptions.StartIndex || 0));
	const tmpSheets = [];
	let tmpCells = [];
	let tmpCellIndex = tmpStart;
	for (const tmpHTML of tmpRendered)
	{
		const tmpColumn = tmpCellIndex % pPreset.Columns;
		const tmpRow = Math.floor(tmpCellIndex / pPreset.Columns);
		tmpCells.push(`<div class="hlqr-sheet-cell" style="left:${mm(pPreset.LeftMM + (tmpColumn * pPreset.PitchXMM))};top:${mm(pPreset.TopMM + (tmpRow * pPreset.PitchYMM))};">${tmpHTML}</div>`);
		tmpCellIndex++;
		if (tmpCellIndex >= tmpPerSheet)
		{
			tmpSheets.push(tmpCells.join(''));
			tmpCells = [];
			tmpCellIndex = 0;
		}
	}
	if (tmpCells.length > 0)
	{
		tmpSheets.push(tmpCells.join(''));
	}
	const tmpHTML = tmpSheets.map((pCells) => `<div class="hlqr-print-page hlqr-print-sheet" style="width:${mm(pPreset.PageWidthMM)};height:${mm(pPreset.PageHeightMM)};">${pCells}</div>`).join('');
	return { HTML: tmpHTML, PageCSS: `@page { size: ${mm(pPreset.PageWidthMM)} ${mm(pPreset.PageHeightMM)}; margin: 0; }`, Plans: tmpPlans };
}

/**
 * One label drawn at the printer's resolution, for native print bridges that take an image.
 *
 * @param {import('./Label-Media-Presets.js').MediaPreset} pPreset
 * @param {LabelContent} pLabel
 * @param {Object} [pOptions] - Passed to planLabel.
 * @param {Document} [pDocument] - Defaults to the global document.
 * @return {HTMLCanvasElement}
 */
function renderLabelCanvas(pPreset, pLabel, pOptions, pDocument)
{
	const tmpDocument = pDocument || document;
	const tmpPlan = planLabel(pPreset, pLabel, pOptions);
	const tmpDotsPerMM = pPreset.DPI / 25.4;
	const tmpCanvas = tmpDocument.createElement('canvas');
	tmpCanvas.width = Math.round(pPreset.LabelWidthMM * tmpDotsPerMM);
	tmpCanvas.height = Math.round(pPreset.LabelHeightMM * tmpDotsPerMM);
	const tmpContext = tmpCanvas.getContext('2d');
	tmpContext.fillStyle = '#fff';
	tmpContext.fillRect(0, 0, tmpCanvas.width, tmpCanvas.height);

	const tmpMargin = Math.round((pPreset.MarginMM || 2) * tmpDotsPerMM);
	const tmpCodeDots = Math.round(tmpPlan.CodeMM * tmpDotsPerMM);
	libMatrix.drawToCanvas(tmpContext, tmpPlan.Matrix, tmpMargin, tmpMargin, tmpPlan.DotsPerModule);

	const tmpFontPixels = Math.round(tmpPlan.FontPT / 72 * pPreset.DPI);
	const tmpLineHeight = Math.round(tmpFontPixels * 1.25);
	let tmpTextX = tmpMargin;
	let tmpTextY = tmpMargin + tmpCodeDots + Math.round(GAP_MM * tmpDotsPerMM);
	let tmpTextWidth = tmpCanvas.width - (2 * tmpMargin);
	if (pPreset.Layout === 'Side')
	{
		tmpTextX = tmpMargin + tmpCodeDots + Math.round(GAP_MM * tmpDotsPerMM);
		tmpTextY = tmpMargin;
		tmpTextWidth = tmpCanvas.width - tmpTextX - tmpMargin;
	}
	tmpContext.fillStyle = '#000';
	tmpContext.textBaseline = 'top';
	(pLabel.Lines || []).forEach((pLine, pIndex) =>
	{
		tmpContext.font = `${pIndex === 0 ? '700 ' : ''}${tmpFontPixels}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
		tmpContext.fillText(fitText(tmpContext, String(pLine), tmpTextWidth), tmpTextX, tmpTextY + (pIndex * tmpLineHeight));
	});
	return tmpCanvas;
}

/**
 * @param {CanvasRenderingContext2D} pContext
 * @param {string} pText
 * @param {number} pWidth
 * @return {string} the text, shortened with an ellipsis to fit
 */
function fitText(pContext, pText, pWidth)
{
	if (pContext.measureText(pText).width <= pWidth)
	{
		return pText;
	}
	let tmpText = pText;
	while (tmpText.length > 1 && pContext.measureText(`${tmpText}…`).width > pWidth)
	{
		tmpText = tmpText.slice(0, -1);
	}
	return `${tmpText}…`;
}

/** Styles for labels on screen and in print. */
const LABEL_CSS = `
.hlqr-label { box-sizing: border-box; display: flex; gap: ${GAP_MM}mm; background: #fff; color: #000; overflow: hidden; font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
.hlqr-label--side { flex-direction: row; align-items: center; }
.hlqr-label--stack { flex-direction: column; align-items: center; }
.hlqr-label-code { flex: 0 0 auto; line-height: 0; }
.hlqr-label-text { flex: 1 1 auto; min-width: 0; line-height: 1.2; }
.hlqr-label--stack .hlqr-label-text { width: 100%; text-align: center; }
.hlqr-label-line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hlqr-label-line--first { font-weight: 700; }
.hlqr-label-image { position: relative; width: 100%; height: 100%; overflow: hidden; }
.hlqr-print-page { position: relative; overflow: hidden; break-after: page; page-break-after: always; background: #fff; }
.hlqr-sheet-cell { position: absolute; }
`;

module.exports =
{
	planLabel,
	isImageTurned,
	renderLabelHTML,
	renderPrintJob,
	renderLabelCanvas,
	LABEL_CSS,
};
